/**
 * Vorlage „Wasser aufkochen“.
 *
 * Topf auf dem Gasherd. Der Knopf wird aufgedreht, die Flammen gehen an,
 * erst steigen einzelne Bläschen, dann sprudelt das Wasser und dampft. Auf
 * Wunsch kommt Salz hinein. Zum Schluss wird der Herd wieder ausgeschaltet,
 * damit die Schleife ruhig neu beginnt.
 */
import { ease, segment } from '../geometry.ts';
import { STROKE } from '../palette.ts';
import { compact, faded, shape } from '../scene.ts';
import { bubbles, falling, steam, surfaceLine } from '../parts/effects.ts';
import { saltShaker } from '../parts/kitchen.ts';
import { arcArrow, arrow, counter, doneMark } from '../parts/stage.ts';
import { GRATE_Y, stove, stoveKnob } from '../parts/stove.ts';
import { contents, pot } from '../parts/vessels.ts';
import { booleanValue, defineTemplate } from '../template.ts';

/** Zeitplan. */
const TIME = {
  knobOn: [0.02, 0.08],
  boil: [0.1, 0.6],
  done: [0.62, 0.66],
  salt: [0.66, 0.84],
  knobOff: [0.88, 0.95],
  calm: [0.88, 0.99],
} as const;

/** Topf. */
const POT = { cx: 104, width: 112, height: 48 } as const;
const RIM_Y = GRATE_Y - POT.height;

/** Wasserstand: 0 randvoll, 1 Boden. */
const LEVEL = 0.24;

/** Lage des Drehknopfs. */
const KNOB_X = 206;

/** Vorlage „Wasser aufkochen“. */
export const stoveBoilTemplate = defineTemplate({
  id: 'stove.boil',
  group: 'stove',
  title: 'Wasser aufkochen',
  description:
    'Ein Topf mit Wasser steht auf starker Flamme. Erst steigen einzelne Bläschen auf, dann sprudelt das Wasser kräftig und dampft.',
  durationSeconds: 6,
  keywords: [
    'aufkochen',
    'zum kochen bringen',
    'sprudelnd',
    'salzwasser',
    'wasser kochen',
    'kochendes wasser',
  ],
  stillTime: 0.64,
  parameters: [{ kind: 'boolean', name: 'salt', label: 'Salz zugeben', defaultValue: false }],
  inferValues(text) {
    return text.toLowerCase().includes('salz') ? { salt: true } : {};
  },
  scene(values, t) {
    const salt = booleanValue(values, 'salt', false);
    const power = ease.inOutCubic(segment(t, ...TIME.knobOn)) - ease.inOutCubic(segment(t, ...TIME.knobOff));
    const strength =
      ease.inOutSine(segment(t, ...TIME.boil)) * (1 - ease.inOutCubic(segment(t, ...TIME.calm)));
    const vessel = pot(POT.cx, RIM_Y, POT.width, POT.height);
    const surfaceY = RIM_Y + vessel.depth * LEVEL;
    const rx = vessel.widthAt(LEVEL);
    const ry = vessel.ry * (rx / vessel.rx);
    const burner = stove(POT.cx, power, t);

    const water = contents(vessel, LEVEL, 'tint', [
      shape(surfaceLine(POT.cx - rx, surfaceY - ry * 0.2, rx * 2, 0.6 + strength * 1.4, t * 2, 3), {
        stroke: 'shade',
        strokeWidth: STROKE.fine,
      }),
      ...bubbles(POT.cx - rx, surfaceY - ry * 0.9, rx * 2, ry * 1.8, strength, t, 5),
    ]);

    const saltOpacity = salt
      ? segment(t, TIME.salt[0], TIME.salt[0] + 0.03) * (1 - segment(t, TIME.salt[1] - 0.03, TIME.salt[1]))
      : 0;
    const done = segment(t, ...TIME.done) * (1 - segment(t, 0.86, 0.9));
    return compact([
      ...counter(),
      ...burner.back,
      ...vessel.back,
      water,
      ...vessel.front,
      ...burner.front,
      ...steam(POT.cx, RIM_Y - 6, t, { count: 3, spread: 24, height: 30, strength: strength * 0.95 }),
      faded(saltOpacity, [
        saltShaker(POT.cx + 34, 22),
        ...falling(POT.cx + 26, 34, surfaceY - 2, 14, 9, t, { seed: 4, size: 1.4, loops: 4, tone: 'ink' }),
      ]),
      stoveKnob(KNOB_X, power),
      faded(ease.outBack(done), [doneMark(206, 40, 1.2)]),
    ]);
  },
  hints() {
    return [
      arrow([
        { x: POT.cx - 30, y: 58 },
        { x: POT.cx - 30, y: 34 },
      ]),
      arrow([
        { x: POT.cx + 30, y: 58 },
        { x: POT.cx + 30, y: 34 },
      ]),
      arcArrow(KNOB_X, 149, 13, 13, 200, 330),
    ];
  },
});
